import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Player Portal's own sidebar shell — deliberately mirrors the admin
// Layout.jsx (same dark green, same nav-button styling, same structure) so
// the portal reads as one consistent app instead of a set of separate
// mobile-card pages bolted on the side.
const NAV_ITEMS = [
  { label: "Dashboard", path: "/player/dashboard" },
  { label: "Declare Your Marker", path: "/player/declare-marker" },
  { label: "Verify a Playing Partner", path: "/player/hcp-verify" },
  { label: "My Results", path: "/player/my-results" },
];

export default function PlayerLayout({ children, instructions }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [player, setPlayer] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const token = sessionStorage.getItem("player_token");
    if (!token) { navigate("/player/login"); return; }
    fetch("/api/player/dashboard", { headers: { Authorization: "Bearer " + token } })
      .then(r => { if (!r.ok) { navigate("/player/login"); return null; } return r.json(); })
      .then(d => { if (d) setPlayer(d.player); })
      .catch(() => {});
  }, []);

  const logout = () => {
    sessionStorage.removeItem("player_token");
    sessionStorage.removeItem("player_name");
    navigate("/player/login");
  };

  const isPinHigh = player?.society_id === 7;
  const visibleNav = NAV_ITEMS.filter(item => !item.pinHighOnly || isPinHigh);

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", backgroundColor: "#f3f4f6", paddingTop: isMobile ? "48px" : "0" }}>
      {isMobile && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "48px", backgroundColor: "#14532d", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px", zIndex: 300 }}>
          <div style={{ color: "white", fontWeight: "bold", fontSize: "14px" }}>⛳ Player Portal</div>
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: "none", border: "none", color: "white", fontSize: "24px", cursor: "pointer", padding: "4px 8px" }}>
            {menuOpen ? "✕" : "☰"}
          </button>
        </div>
      )}

      {isMobile && menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", top: "48px", left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 199 }} />
      )}

      <div style={{
        width: isMobile ? "280px" : "240px",
        backgroundColor: "#14532d",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        position: isMobile ? "fixed" : "relative",
        top: isMobile ? "48px" : "0",
        left: 0, bottom: 0, zIndex: 200,
        transform: isMobile ? (menuOpen ? "translateX(0)" : "translateX(-100%)") : "none",
        transition: "transform 0.25s ease",
        overflowY: "auto",
      }}>
        {!isMobile && (
          <div style={{ padding: "16px 12px", borderBottom: "1px solid #166534" }}>
            <div style={{ color: "white", fontWeight: "bold", fontSize: "15px", display: "flex", alignItems: "center", gap: "6px" }}>⛳ Player Portal</div>
            {player?.society_name && <div style={{ color: "white", fontSize: "14px", fontWeight: "800", marginTop: "10px" }}>{player.society_name}</div>}
            {player?.name && <div style={{ color: "#86efac", fontSize: "12px", marginTop: "2px" }}>👤 {player.name}</div>}
          </div>
        )}
        <nav style={{ flex: 1, paddingTop: "8px", paddingBottom: "8px" }}>
          {visibleNav.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                style={{ width: "100%", textAlign: "left", padding: "12px 16px", fontSize: "14px", fontWeight: "500", border: "none", cursor: "pointer", minHeight: "44px", backgroundColor: isActive ? "#16a34a" : "transparent", color: isActive ? "white" : "#dcfce7" }}>
                {item.label}
              </button>
            );
          })}
        </nav>
        <div style={{ padding: "8px", borderTop: "1px solid #166534", display: "flex", flexDirection: "column", gap: "6px" }}>
          <button onClick={() => navigate("/player/change-password")} style={{ width: "100%", backgroundColor: "#ca8a04", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>Change Password</button>
          <button onClick={logout} style={{ width: "100%", backgroundColor: "#4b5563", color: "white", fontSize: "12px", fontWeight: "600", borderRadius: "4px", padding: "8px 12px", border: "none", cursor: "pointer", minHeight: "44px" }}>Logout</button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
        <div style={{ display: "flex", gap: "24px", alignItems: "flex-start", padding: "24px", maxWidth: "1100px", margin: "0 auto", flexWrap: "wrap" }}>
          <div style={{ flex: "2 1 420px", minWidth: 0 }}>{children}</div>
          {instructions && (
            <div style={{ flex: "1 1 280px", minWidth: "260px", position: "sticky", top: "24px" }}>
              <div style={{ background: "white", borderRadius: "10px", padding: "18px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                {instructions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
